import React, { useState, useContext } from "react";
import api from '../../api/axiosClient';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from "react-router-dom";
import { CartContext } from "../../components/CartContext";
import { useAuth } from "../../context/AuthContext";

export default function AuthPage() {
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [mobileMode, setMobileMode] = useState(false);
  const [phone, setPhone] = useState("");
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [mobileOtp, setMobileOtp] = useState("");
  const { syncOnLogin } = useContext(CartContext);
  const { login: loginAuth } = useAuth();

  const navigate = useNavigate();
  // 🔹 Function to handle form submission for login/register
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (tab === "login") {
      try {
        console.log("Logging in with", email, password);
        const res = await api.post("/auth/login", {
          username: email,
          password: password,
        });
        console.log("Login response:", res.data);
        loginAuth({
          accessToken: res.data.accessToken ?? res.data.token,
          refreshToken: res.data.refreshToken,
          role: res.data.role,
          accessMask: res.data.accessMask,
          userId: res.data.userId,
          user: res.data.user,
        });
        
        await syncOnLogin();   // merge happens here
        if (res.data.role === "ADMIN") {
          navigate("/admin/dashboard");
        } else {
          navigate("/shop");
        }
      } catch (err) {
        setError("Invalid credentials. Please try again.");
      }
    } else {
      alert("Register functionality not implemented yet!");
    }
  };

  const handleSendMobileOtp = async () => {
    setError("");

    if (!phone || phone.length < 10) {
      return setError("Enter a valid mobile number");
    }

    try {
      await api.post("/auth/send-mobile-otp", { phone });
      setMobileOtpSent(true);
    } catch (err) {
      setError("Failed to send OTP. Try again.");
    }
  };


  const handleVerifyMobileOtp = async () => {
    try {
      const res = await api.post("/auth/verify-mobile-otp", {
        phone,
        otp: mobileOtp,
      });

      loginAuth({
        accessToken: res.data.accessToken ?? res.data.token,
        refreshToken: res.data.refreshToken,
        role: res.data.role,
        accessMask: res.data.accessMask,
        userId: res.data.userId,
        user: res.data.user,
      });

      if (res.data.role === "ADMIN") navigate("/admin/dashboard");
      else navigate("/shop");

    } catch {
      setError("Invalid OTP. Try again.");
    }
  };


  // 🔹 Function to handle Google login logic
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    console.log("Google login success:", credentialResponse);
    const token = credentialResponse.credential; // Google ID token

    try {
      const res = await api.post("/auth/google", { token: credentialResponse.credential });

      loginAuth({
        accessToken: res.data.accessToken ?? res.data.token,
        refreshToken: res.data.refreshToken,
        role: res.data.role,
        accessMask: res.data.accessMask,
        userId: res.data.userId,
        user: res.data.user,
      });
      

      try {
        await syncOnLogin();   // cart merge
      } catch (cartErr) {
        console.error("Cart sync failed:", cartErr);
        // optional: show a different message
        alert("Logged in, but cart sync failed. Please refresh.");
      }

      // Redirect based on role
      if (res.data.role === "ADMIN") {
        navigate("/admin/dashboard");
      } else {
        navigate("/shop");
      }
    } catch (err) {
      console.error("Google auth failed:", err);
      alert("Google login failed");
    }
  };

  const handleGoogleLoginError = () => {
    console.log("Google login failed");
  };


  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 relative overflow-hidden">
      {/* Full-screen background */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "linear-gradient(135deg, #fbcfe8 0%, #bfdbfe 45%, #d8b4fe 100%)",
        }}
      />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md bg-white/60 backdrop-blur-lg rounded-2xl shadow-xl p-6 sm:p-8">

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            <span className="text-blue-600">5</span> BUDDIES
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setTab("login")}
            className={`w-1/2 py-2 font-medium ${tab === "login"
              ? "bg-white text-gray-900 shadow rounded-l-lg"
              : "bg-transparent text-gray-500"
              }`}
          >
            Login
          </button>
          <button
            onClick={() => setTab("register")}
            className={`w-1/2 py-2 font-medium ${tab === "register"
              ? "bg-white text-gray-900 shadow rounded-r-lg"
              : "bg-transparent text-gray-500"
              }`}
          >
            Register
          </button>
        </div>

        {/* Form */}
        <form className="space-y-4" onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
          />

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex justify-between items-center text-xs sm:text-sm">
            <a href="#" className="text-gray-500 hover:underline">
              Forgot password?
            </a>
          </div>

          <button
            type="submit"
            className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-400 to-purple-400 text-white font-semibold shadow-lg hover:opacity-90 transition text-sm sm:text-base"
          >
            {tab === "login" ? "Continue" : "Register"}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center my-6">
          <div className="flex-grow h-px bg-gray-300"></div>
          <span className="px-2 text-gray-500 text-xs sm:text-sm">or</span>
          <div className="flex-grow h-px bg-gray-300"></div>
        </div>

        {/* Social logins */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
          <GoogleLogin
            onSuccess={handleGoogleLoginSuccess}
            onError={handleGoogleLoginError}
          />
          <button className="flex items-center justify-center gap-2 border border-gray-300 px-4 py-2 rounded-lg bg-white hover:shadow w-full sm:w-auto">
            <img
              src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apple/apple-original.svg"
              alt="Apple"
              className="w-5 h-5"
            />
            Apple
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-xs mt-6 text-gray-500 space-x-2">
          <a href="#" className="hover:underline">
            Terms
          </a>
          <span>•</span>
          <a href="#" className="hover:underline">
            Privacy
          </a>
          <span>•</span>
          <a href="#" className="hover:underline">
            Help
          </a>
        </div>

        {/* Character Icon */}
        <div className="absolute -bottom-6 right-4 hidden sm:block">
          <img
            src="https://em-content.zobj.net/thumbs/120/apple/354/waving-hand_1f44b.png"
            alt="Mascot"
            className="w-14 h-14 sm:w-16 sm:h-16"
          />
        </div>
      </div>
    </div>
  );
}
